import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  X,
  User,
  LogOut,
  Bell,
  LifeBuoy,
  Accessibility,
  ChevronRight,
  Inbox,
  Loader2,
  Mail,
} from "lucide-react";
import { useAppSelector } from "@/app/hooks";
import { selectAuth } from "@/features/auth/authSlice";
import { useUserRole } from "@/hooks/useUserRole";
import { canViewProfileMessages } from "@/features/user-profile/utils/profileMessagesAccess";
import { logoutAndRedirect } from "@/lib/api/authService";
import { showApiError, showSuccess } from "@/lib/utils/notifications";
import {
  fetchNotificationCenter,
  fetchUserNotificationDetail,
  markAllNotificationsRead,
  updateProfileNotification,
} from "@/lib/api/notificationService";
import type {
  CenterKind,
  CenterMessage,
  NotificationDetail,
} from "@/types/notification";
import { NotificationDetailPanel } from "./NotificationDetailPanel";
import { SeeMoreMessagesLink } from "./SeeMoreMessagesLink";
import styles from "../styles/userDrawer.module.css";
import "@/features/system-announcement/styles/announcementHtml.css";
import { normalizeAnnouncementDescriptionHtml } from "@/features/system-announcement/utils/normalizeAnnouncementHtml";
import { markReleaseNoteRead } from "@/lib/api/releaseNotesService";
import { markAnnouncementRead } from "@/lib/api/announcementsService";
import {
  fetchNotificationCounts,
  type NotificationCounts,
} from "@/lib/api/notificationCountService";
import {
  VERSION_TYPE_LABELS,
  VERSION_TYPE_COLOR_CLASS,
  buildNotificationPreviewText,
  formatNotificationScheduleDate,
  combinedDrawerBadgeCount,
  isCenterMessageUnread,
  hasUnreadCenterMessages,
  filterCenterMessagesByReadStatus,
  centerMessageKey,
  DRAWER_KIND_LABELS,
  type NotificationReadFilter,
  getUserInitials,
  notificationCreatedAtDate,
} from "../utils/userDrawerHelpers";
import {
  formatNotificationListDate,
  getAvatarTone,
  getNotificationTitle,
  getSenderInitials,
  getSenderName,
  mergeNotificationDetail,
  processReleaseNoteImages,
  resolveCenterKind,
  toUserNotification,
} from "@/features/user-profile/utils/messageCenterHelpers";
import { format } from "date-fns";
import { cn } from "@/lib/utils/helpers";
import type { UserDrawerProps } from "../types/components";

const KIND_BADGE_CLASS: Record<CenterKind, string> = {
  user: "bg-sky-50 text-sky-700",
  whats_new: "bg-emerald-50 text-emerald-700",
  system: "bg-amber-50 text-amber-700",
};

function KindBadge({ kind }: { kind: CenterKind }) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[10px] font-semibold",
        KIND_BADGE_CLASS[kind],
      )}
    >
      {DRAWER_KIND_LABELS[kind]}
    </span>
  );
}

function SenderAvatar({
  firstName,
  lastName,
  size = "md",
}: {
  firstName?: string | null;
  lastName?: string | null;
  size?: "sm" | "md" | "lg";
}) {
  const name = getSenderName(firstName, lastName);
  return (
    <div
      className={cn(
        "grid shrink-0 place-items-center rounded-xl font-bold",
        size === "sm" && "size-10 text-sm",
        size === "md" && "size-12 text-base",
        size === "lg" && "size-14 text-lg",
        getAvatarTone(name),
      )}
    >
      {getSenderInitials(firstName, lastName)}
    </div>
  );
}

export function UserDrawer({ isOpen, onClose, onCountsRefresh }: UserDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const { user } = useAppSelector(selectAuth);
  const { roleName } = useUserRole();
  const allowUserNotification = canViewProfileMessages(roleName);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    // Same as applicant logout: do not clear Redux auth before redirect —
    // logoutAndRedirect handles cleanup + navigates to login.
    await logoutAndRedirect();
  };

  const handleLogoutCancel = () => {
    if (isLoggingOut) return;
    setShowLogoutModal(false);
  };
  const [view, setView] = useState<"menu" | "notifications">("menu");
  const [notifications, setNotifications] = useState<CenterMessage[]>([]);
  const [notificationFilter, setNotificationFilter] =
    useState<NotificationReadFilter>("all");
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState<CenterMessage | null>(
    null,
  );
  const [notificationDetail, setNotificationDetail] =
    useState<NotificationDetail | null>(null);
  const [loadingNotificationDetail, setLoadingNotificationDetail] =
    useState(false);
  const [actingNotification, setActingNotification] = useState<{
    key: string;
    action: "read" | "unread";
  } | null>(null);
  const [notificationCounts, setNotificationCounts] =
    useState<NotificationCounts | null>(null);
  const myNotificationsBadge = combinedDrawerBadgeCount(notificationCounts);

  const loadNotificationCounts = useCallback(async () => {
    try {
      const counts = await fetchNotificationCounts(allowUserNotification);
      setNotificationCounts(counts);
      await onCountsRefresh?.();
    } catch (error: unknown) {
      console.error("Failed to fetch notification counts", error);
    }
  }, [allowUserNotification, onCountsRefresh]);

  useEffect(() => {
    setAvatarBroken(false);
  }, [user?.profile_pic]);

  // Reset view when closed; load badge counts when opened
  useEffect(() => {
    if (!isOpen) {
      setView("menu");
      setAvatarBroken(false);
      setNotifications([]);
      setNotificationFilter("all");
      setSelectedCenter(null);
      setNotificationDetail(null);
      setLoadingNotifications(false);
      setLoadingNotificationDetail(false);
      setActingNotification(null);
      return;
    }
    void loadNotificationCounts();
  }, [isOpen, loadNotificationCounts]);

  // Refresh badge counts while drawer stays open (Angular notificationupdate subscription)
  useEffect(() => {
    if (!isOpen) return;

    const intervalId = window.setInterval(() => {
      void loadNotificationCounts();
    }, 30_000);

    const handleFocus = () => {
      void loadNotificationCounts();
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isOpen, loadNotificationCounts]);

  // Release note image preview (matches Angular right-drawer filePreview)
  useEffect(() => {
    if (selectedCenter?.kind !== "whats_new") return;

    const handleImagePreview = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement)) return;
      if (target.getAttribute("alt") !== "filepreviewpopup") return;
      const src = target.getAttribute("src");
      if (!src) return;
      window.open(
        `/file-handlers?path=${encodeURIComponent(src)}`,
        "_blank",
        "noopener",
      );
    };

    document.addEventListener("click", handleImagePreview);
    return () => document.removeEventListener("click", handleImagePreview);
  }, [selectedCenter?.kind]);

  const reloadNotifications = useCallback(async () => {
    try {
      const response = await fetchNotificationCenter({
        folder: "all",
        source: "all",
        page: 1,
        perPage: 100,
        allowAllKind: allowUserNotification,
      });
      setNotifications(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
    }
  }, [allowUserNotification]);

  const handleShowNotifications = async () => {
    setView("notifications");
    setNotificationFilter("all");
    setSelectedCenter(null);
    setNotificationDetail(null);
    setLoadingNotifications(true);
    try {
      const response = await fetchNotificationCenter({
        folder: "all",
        source: "all",
        page: 1,
        perPage: 100,
        allowAllKind: allowUserNotification,
      });
      setNotifications(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const filteredNotifications = useMemo(
    () => filterCenterMessagesByReadStatus(notifications, notificationFilter),
    [notifications, notificationFilter],
  );
  const unreadCount = useMemo(
    () => notifications.filter(isCenterMessageUnread).length,
    [notifications],
  );
  const readCount = notifications.length - unreadCount;

  const markLocalRead = (item: CenterMessage, isRead: boolean) => {
    const nextRead = isRead ? "1" : null;
    setNotifications((prev) =>
      prev.map((row) =>
        row.kind === item.kind && row.id === item.id
          ? { ...row, is_read: nextRead }
          : row,
      ),
    );
    setSelectedCenter((prev) =>
      prev && prev.kind === item.kind && prev.id === item.id
        ? { ...prev, is_read: nextRead }
        : prev,
    );
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await markAllNotificationsRead();
      await reloadNotifications();
      void loadNotificationCounts();
      showSuccess("All notifications marked 'Read' successfully!");
    } catch (error: unknown) {
      showApiError(error);
    }
  };

  const handleNotificationSelect = (item: CenterMessage) => {
    const kind = resolveCenterKind(item.kind);
    const selected = { ...item, kind };
    setSelectedCenter(selected);
    setNotificationDetail(
      kind === "user" ? toUserNotification(selected) : null,
    );
    setLoadingNotificationDetail(false);

    void (async () => {
      try {
        if (kind === "whats_new") {
          if (isCenterMessageUnread(item)) {
            await markReleaseNoteRead(item.id);
          }
        } else if (kind === "system") {
          if (isCenterMessageUnread(item)) {
            await markAnnouncementRead(item.id);
          }
        } else {
          const detail = await fetchUserNotificationDetail(item.id);
          setNotificationDetail((prev) =>
            mergeNotificationDetail(prev ?? toUserNotification(selected), detail),
          );
        }
        markLocalRead(item, true);
        void loadNotificationCounts();
      } catch (error: unknown) {
        console.error("Failed to enrich notification detail", error);
        markLocalRead(item, true);
      }
    })();
  };

  const handleNotificationDetailBack = () => {
    setSelectedCenter(null);
    setNotificationDetail(null);
    setLoadingNotificationDetail(false);
    void reloadNotifications();
  };

  const handleNotificationStatusChange = async (
    item: CenterMessage,
    action: "read" | "unread",
  ) => {
    if (actingNotification) return;
    if (item.kind === "user" && !user?.user_id) return;
    const key = centerMessageKey(item);
    setActingNotification({ key, action });
    try {
      if (item.kind === "whats_new") {
        await markReleaseNoteRead(item.id, action === "read" ? 1 : 0);
      } else if (item.kind === "system") {
        await markAnnouncementRead(item.id, action === "read" ? 1 : 0);
      } else {
        await updateProfileNotification(item.id, {
          userId: user!.user_id,
          [action]: 1,
        });
      }
      markLocalRead(item, action === "read");
      void loadNotificationCounts();
    } catch (error: unknown) {
      showApiError(error);
    } finally {
      setActingNotification(null);
    }
  };

  const handleDrawerBack = () => {
    if (
      view === "notifications" &&
      (notificationDetail || selectedCenter || loadingNotificationDetail)
    ) {
      handleNotificationDetailBack();
      return;
    }
    setView("menu");
    void loadNotificationCounts();
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if logging out or confirm dialog is open
      if (isLoggingOut || showLogoutModal) return;

      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isLoggingOut, showLogoutModal]);

  // Keep mounted while logout confirm is open so the dialog is not unmounted with the drawer
  if (!isOpen && !showLogoutModal) return null;

  return (
    <div
      data-testid="invitee-user-drawer-root"
      className={
        isOpen
          ? "fixed inset-0 z-[9999] overflow-hidden"
          : "fixed inset-0 z-[9999] pointer-events-none"
      }
    >
      {/* Overlay — only when drawer is open */}
      {isOpen ? (
        <div
          data-testid="invitee-user-drawer-backdrop"
          className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity pointer-events-auto"
        />
      ) : null}

      {/* Drawer Panel */}
      {isOpen ? (
      <div
        ref={drawerRef}
        data-testid="invitee-user-drawer-panel"
        className={`absolute inset-y-0 right-0 flex w-full max-w-[400px] transform flex-col overflow-hidden bg-white shadow-2xl transition-transform duration-300 ease-in-out pointer-events-auto ${styles.drawerPanel}`}
      >
        {/* Header Section — close on the right so it never overlaps the mobile nav hamburger */}
        <div
          data-testid="invitee-user-drawer-header"
          className="flex min-h-[72px] items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white px-4 py-3 sm:px-6"
        >
          {view !== "menu" ? (
            <button
              type="button"
              data-testid="invitee-user-drawer-back-to-menu-button"
              onClick={handleDrawerBack}
              className="flex min-w-0 items-center gap-2 text-slate-500 transition-colors hover:text-slate-900"
            >
              <ChevronRight className="h-5 w-5 shrink-0 rotate-180" />
              {view === "notifications" && (
                <>
                  <Bell className="h-5 w-5 shrink-0 text-[rgb(21_151_255)] fill-[rgb(21_151_255)]/10" />
                  <span className="truncate font-bold text-lg text-slate-900">
                    My Notifications
                  </span>
                </>
              )}
            </button>
          ) : (
            <p className="text-base font-bold text-slate-900">
              Profile
            </p>
          )}

          <button
            type="button"
            data-testid="invitee-user-drawer-close-button"
            onClick={onClose}
            disabled={isLoggingOut}
            className="ml-auto shrink-0 rounded-full border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Area */}
        <div
          data-testid="invitee-user-drawer-content"
          className="relative flex-1 overflow-y-auto bg-slate-50/60"
        >
          {view === "menu" ? (
            <div data-testid="invitee-user-drawer-menu" className="space-y-4 p-4 pb-6 sm:p-5">
              <div
                data-testid="invitee-user-drawer-profile-section"
                className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center gap-3.5">
                  <div className="relative shrink-0">
                    <div
                      data-testid="invitee-user-drawer-avatar"
                      className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-gradient-to-br from-sky-100 to-slate-100 text-base font-bold tracking-wide text-sky-700 shadow-sm"
                    >
                      {user?.profile_pic && !avatarBroken ? (
                        <img
                          src={user.profile_pic}
                          alt=""
                          className="h-full w-full object-cover"
                          onError={() => setAvatarBroken(true)}
                        />
                      ) : (
                        <span data-testid="invitee-user-drawer-avatar-initials">
                          {getUserInitials(user?.first_name, user?.last_name)}
                        </span>
                      )}
                    </div>
                    <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-500">Hello</p>
                    <h3 className="truncate text-lg font-bold tracking-tight text-slate-900">
                      {user?.first_name} {user?.last_name}
                    </h3>
                  </div>
                </div>

                <div className="mt-4 flex gap-2.5">
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-account-button"
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[rgb(21_151_255)] px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0d7acc]"
                    onClick={() => {
                      onClose();
                      navigate("/user/profile");
                    }}
                  >
                    <User className="h-4 w-4" />
                    Account
                  </button>
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-logout-button"
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-rose-300 bg-white px-3 py-2.5 text-sm font-semibold text-rose-500 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={isLoggingOut}
                    onClick={handleLogoutClick}
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              </div>

              <div className="space-y-2.5">
                <button
                  type="button"
                  data-testid="invitee-user-drawer-notifications-button"
                  className="group flex w-full items-start gap-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm transition hover:border-sky-200 hover:shadow-md"
                  onClick={handleShowNotifications}
                >
                  <div className="relative shrink-0">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-[rgb(21_151_255)]">
                      <Bell className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    {myNotificationsBadge > 0 ? (
                      <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-rose-500 px-1 text-[10px] font-bold text-white">
                        {myNotificationsBadge}
                      </span>
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-bold text-slate-900 transition group-hover:text-[rgb(21_151_255)]">
                        My Notifications
                      </h4>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-[rgb(21_151_255)]" />
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      Mentions, What's New updates, and system announcements.
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  data-testid="invitee-user-drawer-help-center-button"
                  className="group flex w-full items-start gap-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm transition hover:border-rose-200 hover:shadow-md"
                  onClick={() => {
                    onClose();
                    navigate("/help-about");
                  }}
                >
                  <div className="relative shrink-0">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                      <LifeBuoy className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-bold text-slate-900 transition group-hover:text-rose-500">
                        Help Center
                      </h4>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-rose-400" />
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      Guides, tutorials, and knowledgebase articles.
                    </p>
                  </div>
                </button>
              </div>
            </div>
          ) : (
            /* Unified feed: mentions + What's New + system announcements */
            <div
              data-testid="invitee-user-drawer-notifications-view"
              className="absolute inset-0 overflow-y-auto bg-gray-50/50"
            >
              {!selectedCenter && (
                <div className="bg-white px-4 py-3 border-b border-gray-100 sticky top-0 z-10 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-bold text-gray-900">Notifications</h4>
                    <div className="flex items-center gap-3">
                      {hasUnreadCenterMessages(notifications) && (
                        <button
                          type="button"
                          data-testid="invitee-user-drawer-mark-all-read-button"
                          className="text-xs font-bold text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline"
                          onClick={() => void handleMarkAllNotificationsRead()}
                        >
                          Mark All As Read
                        </button>
                      )}
                    </div>
                  </div>
                  <div
                    data-testid="invitee-user-drawer-notification-filters"
                    className="mt-3 flex rounded-lg bg-gray-100 p-1"
                    role="tablist"
                    aria-label="Notification filters"
                  >
                    {(
                      [
                        { id: "all", label: "All", count: notifications.length },
                        { id: "unread", label: "Unread", count: unreadCount },
                        { id: "read", label: "Read", count: readCount },
                      ] as const
                    ).map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={notificationFilter === tab.id}
                        data-testid={`invitee-user-drawer-notification-filter-${tab.id}`}
                        onClick={() => setNotificationFilter(tab.id)}
                        className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
                          notificationFilter === tab.id
                            ? "bg-white text-[rgb(21_151_255)] shadow-sm"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        {tab.label}
                        <span className="ml-1 font-medium text-gray-400">
                          {tab.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedCenter?.kind === "whats_new" ? (
                <div className="p-4">
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-release-note-back"
                    onClick={handleNotificationDetailBack}
                    className="mb-4 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back to list
                  </button>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="mb-3">
                      <KindBadge kind="whats_new" />
                    </div>
                    <div className="flex items-start gap-3">
                      <SenderAvatar
                        firstName={selectedCenter.first_name}
                        lastName={selectedCenter.last_name}
                        size="lg"
                      />
                      <div className="min-w-0">
                        <h5 className="font-bold text-gray-900 break-words">
                          {selectedCenter.title}
                        </h5>
                        {selectedCenter.version_type ? (
                          <p
                            className={`text-sm font-medium ${VERSION_TYPE_COLOR_CLASS[selectedCenter.version_type] ?? "text-gray-600"}`}
                          >
                            {VERSION_TYPE_LABELS[selectedCenter.version_type] ??
                              selectedCenter.version_type}
                          </p>
                        ) : null}
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedCenter.created_at
                            ? format(
                                notificationCreatedAtDate(selectedCenter.created_at),
                                "MMM dd, yyyy",
                              )
                            : ""}
                        </p>
                      </div>
                    </div>
                    {selectedCenter.body ? (
                      <div
                        className="mt-4 text-sm text-gray-700 prose prose-sm max-w-none min-w-0 break-words [overflow-wrap:anywhere]"
                        dangerouslySetInnerHTML={{
                          __html: processReleaseNoteImages(selectedCenter.body),
                        }}
                      />
                    ) : null}
                    <SeeMoreMessagesLink
                      kind="whats_new"
                      id={selectedCenter.id}
                      onNavigate={onClose}
                    />
                  </div>
                </div>
              ) : selectedCenter?.kind === "system" ? (
                <div className="p-4">
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-announcement-detail-back"
                    onClick={handleNotificationDetailBack}
                    className="mb-4 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back to list
                  </button>
                  <div
                    data-testid="invitee-user-drawer-announcement-detail"
                    className="bg-white rounded-xl border border-gray-100 p-4"
                  >
                    <div className="mb-3">
                      <KindBadge kind="system" />
                    </div>
                    <div className="flex items-start gap-3">
                      <SenderAvatar
                        firstName={selectedCenter.first_name}
                        lastName={selectedCenter.last_name}
                        size="lg"
                      />
                      <div className="min-w-0">
                        <h5 className="font-bold text-gray-900 break-words">
                          {selectedCenter.title}
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedCenter.start_date}
                          {selectedCenter.end_date
                            ? ` to ${selectedCenter.end_date}`
                            : ""}
                        </p>
                      </div>
                    </div>
                    {selectedCenter.body ? (
                      <div
                        className="announcement-html text-sm text-gray-700 mt-4 max-w-none min-w-0 break-words [overflow-wrap:anywhere]"
                        dangerouslySetInnerHTML={{
                          __html: normalizeAnnouncementDescriptionHtml(
                            selectedCenter.body,
                          ),
                        }}
                      />
                    ) : null}
                    <SeeMoreMessagesLink
                      kind="system"
                      id={selectedCenter.id}
                      onNavigate={onClose}
                    />
                  </div>
                </div>
              ) : selectedCenter &&
                resolveCenterKind(selectedCenter.kind) === "user" ? (
                <NotificationDetailPanel
                  mailContent={mergeNotificationDetail(
                    toUserNotification(selectedCenter),
                    notificationDetail,
                  )}
                  detail={mergeNotificationDetail(
                    toUserNotification(selectedCenter),
                    notificationDetail,
                  )}
                  onBack={handleNotificationDetailBack}
                  onCloseDrawer={onClose}
                />
              ) : loadingNotifications ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <p>Loading notifications...</p>
                </div>
              ) : filteredNotifications.length > 0 ? (
                <div className="p-4 space-y-3">
                  {filteredNotifications.map((item, index) => {
                    const unread = isCenterMessageUnread(item);
                    const key = centerMessageKey(item);
                    const isActing = actingNotification?.key === key;
                    const sender = getSenderName(item.first_name, item.last_name);
                    const title =
                      item.kind === "user"
                        ? item.title || getNotificationTitle(item.subject)
                        : item.title;
                    const preview =
                      item.kind === "user"
                        ? buildNotificationPreviewText(toUserNotification(item))
                        : item.preview;
                    return (
                      <div
                        key={key || index}
                        data-testid={`invitee-user-drawer-notification-item-${key}`}
                        className={cn(
                          "relative cursor-pointer overflow-hidden rounded-xl border shadow-sm",
                          unread
                            ? "border-[#d6e8fb] bg-[#eef5ff]"
                            : "border-gray-200 bg-white",
                        )}
                        onClick={() => void handleNotificationSelect(item)}
                      >
                        {unread ? (
                          <span className="absolute inset-y-0 left-0 w-1 bg-[#2f7de1]" />
                        ) : null}
                        <div className="flex items-start gap-3 px-4 py-3.5 pl-5">
                          <div className="flex min-w-0 flex-1 items-start gap-3 text-left">
                            <SenderAvatar
                              firstName={item.first_name}
                              lastName={item.last_name}
                              size="sm"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[15px] font-semibold leading-5 text-[#333333]">
                                {title}
                              </p>
                              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] font-normal leading-4 text-[#888888]">
                                <span className="truncate">
                                  {sender}
                                  {item.created_at
                                    ? ` · ${formatNotificationListDate(item.created_at)}`
                                    : ""}
                                </span>
                                {unread ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-[#dceeff] px-2 py-0.5 text-[11px] font-medium text-[#2f7de1]">
                                    <span className="size-1.5 rounded-full bg-[#2f7de1]" />
                                    New
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center rounded-full bg-[#f0f2f5] px-2 py-0.5 text-[11px] font-normal text-[#65676b]">
                                    Read
                                  </span>
                                )}
                              </p>
                              {preview ? (
                                <p className="mt-1 truncate text-[13px] font-normal leading-4 text-[#9a9a9a]">
                                  {preview}
                                </p>
                              ) : null}
                              {item.kind === "user" &&
                              (item.notes_for === "TRAINING" ||
                                item.notes_for === "MEETING") &&
                              item.start_date ? (
                                <p className="mt-1 text-[13px] font-medium text-[#555555]">
                                  Starts On{" "}
                                  {formatNotificationScheduleDate(
                                    item.start_date,
                                    "date",
                                  )}{" "}
                                  at{" "}
                                  {formatNotificationScheduleDate(
                                    item.start_date,
                                    "time",
                                  )}
                                </p>
                              ) : null}
                              {item.kind === "user" &&
                              item.notes_for === "TODO" &&
                              item.end_date ? (
                                <p className="mt-1 text-[13px] font-medium text-[#555555]">
                                  Due On{" "}
                                  {formatNotificationScheduleDate(
                                    item.end_date,
                                    "date",
                                  )}{" "}
                                  at{" "}
                                  {formatNotificationScheduleDate(
                                    item.end_date,
                                    "time",
                                  )}
                                </p>
                              ) : null}
                              <div className="mt-2">
                                <KindBadge kind={item.kind} />
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            title={unread ? "Move to Read" : "Move to unread"}
                            aria-label={unread ? "Move to Read" : "Move to unread"}
                            data-testid={
                              unread
                                ? `invitee-user-drawer-notification-read-${key}`
                                : `invitee-user-drawer-notification-unread-${key}`
                            }
                            disabled={isActing}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleNotificationStatusChange(
                                item,
                                unread ? "read" : "unread",
                              );
                            }}
                            className="mt-0.5 shrink-0 text-slate-400 hover:text-[#1597ff] disabled:opacity-40"
                          >
                            {isActing ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : unread ? (
                              <Mail className="h-5 w-5" />
                            ) : (
                              <Inbox className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 px-6 text-center">
                  <Bell className="h-12 w-12 stroke-1 mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">
                    {notificationFilter === "unread"
                      ? "No unread messages"
                      : notificationFilter === "read"
                        ? "No read messages"
                        : "No notifications"}
                  </p>
                  <p className="text-sm mt-1">
                    {notificationFilter === "all"
                      ? "You're all caught up!"
                      : "Try a different filter to see more messages."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          data-testid="invitee-user-drawer-footer"
          className="p-4 flex justify-end"
        >
          <button
            type="button"
            data-testid="invitee-user-drawer-accessibility-button"
            className="h-10 w-10 bg-[#344675] hover:bg-[#2c3b63] rounded-full flex items-center justify-center text-white transition-colors shadow-lg"
          >
            <Accessibility className="h-5 w-5" />
          </button>
        </div>
      </div>
      ) : null}

      {/* Confirm Logout — same as applicant sidebar (LR / dashboard roles) */}
      {showLogoutModal &&
        createPortal(
          <div
            data-testid="invitee-user-drawer-logout-modal"
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 sm:p-6 pointer-events-auto"
          >
            <div
              className="relative w-full max-w-md max-h-[min(90vh,calc(100vh-2rem))] overflow-auto rounded-xl bg-white p-4 shadow-xl sm:p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="lr-logout-dialog-title"
            >
              <button
                type="button"
                data-testid="invitee-user-drawer-logout-modal-close-button"
                onClick={handleLogoutCancel}
                disabled={isLoggingOut}
                className="absolute right-3 top-3 rounded-md p-1 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50 sm:right-4 sm:top-4"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="mb-4 flex items-center gap-3 pr-8">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                  <LogOut className="h-5 w-5 text-red-600" aria-hidden />
                </div>
                <h3
                  id="lr-logout-dialog-title"
                  className="text-base font-bold text-gray-900 sm:text-lg"
                >
                  Confirm Logout
                </h3>
              </div>
              <p className="mb-6 text-sm text-gray-700">
                Are you sure you want to logout? You will need to login again to
                access your account.
              </p>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
                <button
                  type="button"
                  data-testid="invitee-user-drawer-logout-cancel-button"
                  onClick={handleLogoutCancel}
                  disabled={isLoggingOut}
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 sm:w-auto sm:py-2"
                >
                  No, Cancel
                </button>
                <button
                  type="button"
                  data-testid="invitee-user-drawer-logout-confirm-button"
                  onClick={() => void handleLogoutConfirm()}
                  disabled={isLoggingOut}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50 sm:w-auto sm:py-2"
                >
                  {isLoggingOut ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Logging out...
                    </>
                  ) : (
                    "Yes, Logout"
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

    </div>
  );
}
